import React from 'react';
import { Batiment } from '../../../../shared/types/game';
import { BUILDING_INTERVENTION_SPAWN_RADIUS_KM } from '../../../../shared/constants/gameMap';
import { useBuildingCapacity } from './useBuildingCapacity';

/**
 * Onglet « Informations » — visuel du prototype (`buildings.js` tabInfo) :
 * blocs Identité / Activité / Capacités / Historique en langage .oscockpit,
 * en conservant toutes les fonctions (renommer, activer/désactiver, extensions).
 */

interface BuildingInfoTabProps {
  building: Batiment;
  address?: string;
  ctaName?: string;
  calculatedLevel: number;
  totalCapacity: number;
  totalVehiclesCapacity: number;
  timeRemainingPersonnel: number | null;
  timeRemainingVehicules: number | null;
  handleStartAugmentation: (type: 'personnel' | 'vehicules', places?: number) => void;
  onBuildingUpdate?: (updatedBuilding: Batiment) => void;
  isEditingName: boolean;
  editedName: string;
  onStartEditName: () => void;
  onChangeEditedName: (v: string) => void;
  onSaveName: () => void;
  onCancelEditName: () => void;
}

const TYPE_LABEL: Record<string, string> = {
  pompier: 'Caserne de pompiers',
  hopital: 'Hôpital',
  fdo: "Forces de l'ordre",
  cta: 'Centre de traitement des alertes',
  prison: 'Prison',
};

const pct = (a: number, b: number) => (b > 0 ? Math.min(100, Math.round((a / b) * 100)) : 0);

const BuildingInfoTab: React.FC<BuildingInfoTabProps> = ({
  building,
  address,
  ctaName,
  calculatedLevel,
  totalVehiclesCapacity,
  timeRemainingPersonnel,
  timeRemainingVehicules,
  handleStartAugmentation,
  onBuildingUpdate,
  isEditingName,
  editedName,
  onStartEditName,
  onChangeEditedName,
  onSaveName,
  onCancelEditName,
}) => {
  const { formatTimeRemaining, CAPACITY_PER_AUGMENTATION, XP_REQUIS_PAR_NIVEAU } = useBuildingCapacity(
    building,
    () => {},
    undefined,
  );

  const isCta = building.type === 'cta';
  const typeLabel = TYPE_LABEL[building.type] || building.type;
  const totalCapa = (building.postes_disponibles ?? 0) + (building.postes_occupes ?? 0);
  const occP = building.postes_occupes ?? 0;
  const occV = building.capacite_vehicules ?? 0;
  const xpCurrent = building.xp ?? 0;
  const xpForLevel = calculatedLevel * XP_REQUIS_PAR_NIVEAU;
  const xpInLevel = xpCurrent % XP_REQUIS_PAR_NIVEAU;
  const xpRemaining = XP_REQUIS_PAR_NIVEAU - xpInLevel;

  const rattachement = isCta
    ? "Ce site est un centre d'appel"
    : building.cta_id
      ? ctaName ?? `CTA #${building.cta_id}`
      : 'Non rattaché';

  return (
    <div className="bgrid">
      {/* ── Identité ── */}
      <div className="block">
        <div className="block__h">Identité du site</div>
        <div className="block__b">
          <dl className="kv">
            <dt>Nom</dt>
            <dd>
              {isEditingName ? (
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => onChangeEditedName(e.target.value)}
                  onBlur={onSaveName}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onSaveName();
                    else if (e.key === 'Escape') onCancelEditName();
                  }}
                  autoFocus
                  style={{ background: '#0b1421', border: '1px solid #2d3f59', borderRadius: 4, color: '#dce5f2', padding: '2px 6px', fontSize: 12 }}
                />
              ) : (
                <>
                  {building.nom}{' '}
                  <button className="btn btn--sm btn--ghost" type="button" onClick={onStartEditName}>Renommer</button>
                </>
              )}
            </dd>
            <dt>Type</dt>
            <dd>{typeLabel}</dd>
            <dt>Localisation</dt>
            <dd className="mono">{address ?? building.localisation ?? '—'}</dd>
            <dt>Rattachement</dt>
            <dd>{rattachement}</dd>
            {!isCta ? (
              <>
                <dt>Rayon</dt>
                <dd className="mono">{BUILDING_INTERVENTION_SPAWN_RADIUS_KM} km</dd>
              </>
            ) : null}
          </dl>
          {onBuildingUpdate ? (
            <div style={{ display: 'flex', gap: 5, marginTop: 8 }}>
              <button
                className={`btn btn--sm ${building.actif ? 'btn--ghost' : 'btn--primary'}`}
                type="button"
                onClick={() => onBuildingUpdate({ ...building, actif: !building.actif, updated_at: new Date().toISOString() })}
              >
                {building.actif ? 'Mettre hors service' : 'Remettre en service'}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {/* ── Activité ── */}
      <div className="block">
        <div className="block__h">Activité</div>
        <div className="block__b">
          <div className="stats4">
            <div className="stat"><b>{calculatedLevel}</b><span>niveau</span></div>
            <div className="stat"><b>{xpCurrent}</b><span>XP cumulée</span></div>
            <div className="stat"><b>{occP}</b><span>en poste</span></div>
            <div className="stat"><b>{building.postes_disponibles ?? 0}</b><span>postes libres</span></div>
          </div>
          <div className="need" style={{ marginTop: 8 }}>
            <span className="need__t">Niveau {calculatedLevel}</span>
            <span className="need__bar"><i style={{ width: `${pct(xpInLevel, XP_REQUIS_PAR_NIVEAU)}%` }} /></span>
            <span className="need__c">{xpInLevel}/{XP_REQUIS_PAR_NIVEAU} XP</span>
          </div>
          <p className="bsub">
            {xpRemaining} XP restants (→ niveau {calculatedLevel + 1}) · +1 XP par intervention terminée · le niveau monte automatiquement.
          </p>
        </div>
      </div>

      {/* ── Capacités ── */}
      {!isCta ? (
        <div className="block">
          <div className="block__h">Capacités <span className="mono muted">+{CAPACITY_PER_AUGMENTATION} par augmentation</span></div>
          <div className="block__b">
            <div className="need">
              <span className="need__t">Personnel</span>
              <span className="need__bar"><i style={{ width: `${pct(occP, totalCapa)}%` }} /></span>
              <span className="need__c">{occP}/{totalCapa}</span>
            </div>
            <div className="need" style={{ marginTop: 5 }}>
              <span className="need__t">Extension personnel <span className="mono muted">({building.augmentations_capacite_personnel ?? 0})</span></span>
              {building.augmentation_personnel_en_cours && timeRemainingPersonnel ? (
                <span className="need__c">⏳ {formatTimeRemaining(timeRemainingPersonnel)}</span>
              ) : (
                <button className="btn btn--sm btn--ghost" type="button" onClick={() => handleStartAugmentation('personnel', 1)}>
                  + Augmenter
                </button>
              )}
            </div>

            {totalVehiclesCapacity > 0 ? (
              <>
                <div className="need" style={{ marginTop: 9 }}>
                  <span className="need__t">Véhicules</span>
                  <span className="need__bar"><i style={{ width: `${pct(occV, totalVehiclesCapacity)}%` }} /></span>
                  <span className="need__c">{occV}/{totalVehiclesCapacity}</span>
                </div>
                <div className="need" style={{ marginTop: 5 }}>
                  <span className="need__t">Extension remise <span className="mono muted">({building.augmentations_capacite_vehicules ?? 0})</span></span>
                  {building.augmentation_vehicules_en_cours && timeRemainingVehicules ? (
                    <span className="need__c">⏳ {formatTimeRemaining(timeRemainingVehicules)}</span>
                  ) : (
                    <button className="btn btn--sm btn--ghost" type="button" onClick={() => handleStartAugmentation('vehicules', 1)}>
                      + Augmenter
                    </button>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* ── Historique ── */}
      <div className="block">
        <div className="block__h">Historique</div>
        <div className="block__b">
          <dl className="kv">
            <dt>Création</dt>
            <dd className="mono">
              {new Date(building.created_at).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
            </dd>
            {building.updated_at ? (
              <>
                <dt>Modification</dt>
                <dd className="mono">
                  {new Date(building.updated_at).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </dd>
              </>
            ) : null}
            {building.cout !== undefined && building.cout > 0 ? (
              <>
                <dt>Coût</dt>
                <dd className="mono">{building.cout.toLocaleString('fr-FR')} €</dd>
              </>
            ) : null}
          </dl>
        </div>
      </div>
    </div>
  );
};

export default BuildingInfoTab;
